import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { formatCNPJ } from '../utils/cnpjHelpers.js';
import { Client } from '../api/entities.js';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card.jsx";
import { Button } from "../components/ui/button.jsx";
import { Badge } from "../components/ui/badge.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar.jsx";
import { Separator } from "../components/ui/separator.jsx";
import { 
  ArrowLeft,
  Building2,
  Phone,
  Mail,
  MapPin,
  Users,
  Cpu,
  FileText,
  Edit,
  Trash2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Star,
  ExternalLink,
  Clock,
  Calendar,
  Settings,
  Network,
  HardDrive,
  Key,
  Database,
  Globe,
  Computer,
  PlusCircle,
  Link2
} from 'lucide-react';
import { safeFormatDate, DATE_FORMATS } from '../utils/dateHelpers.js';

export default function ViewClient() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('id');
  
  const [client, setClient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (clientId) {
      loadClient();
    }
  }, [clientId]);

  const loadClient = async () => {
    setIsLoading(true);
    try {
      const data = await Client.findById(clientId);
      setClient(data);
    } catch (error) {
      console.error('Error loading client:', error);
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(createPageUrl('Clients'))}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="space-y-2">
            <div className="h-8 w-64 bg-muted animate-pulse rounded" />
            <div className="h-4 w-48 bg-muted animate-pulse rounded" />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <div className="h-6 w-32 bg-muted animate-pulse rounded mb-4" />
              <div className="space-y-2">
                <div className="h-4 w-full bg-muted animate-pulse rounded" />
                <div className="h-4 w-3/4 bg-muted animate-pulse rounded" />
              </div>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <div className="h-6 w-32 bg-muted animate-pulse rounded mb-4" />
              <div className="space-y-2">
                <div className="h-4 w-full bg-muted animate-pulse rounded" />
                <div className="h-4 w-3/4 bg-muted animate-pulse rounded" />
              </div>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <Card className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
        <h2 className="text-lg font-medium mb-2">Cliente não encontrado</h2>
        <p className="text-muted-foreground mb-6">
          O cliente solicitado não foi encontrado no sistema.
        </p>
        <Button onClick={() => navigate(createPageUrl('Clients'))}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar para Lista
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      {/* Header Moderno */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 bg-white rounded-xl shadow p-6 border border-gray-100">
        <div className="flex items-center gap-5">
          <Avatar className="h-20 w-20 text-3xl font-bold bg-primary/10">
            <AvatarFallback>
              {(client.corporate_name || client.name || 'CL').substring(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{client.corporate_name || client.name}</h1>
              <Badge className={client.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                {client.status === 'active' ? 'Ativo' : 'Inativo'}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <span><strong>CNPJ:</strong> {formatCNPJ(client.cnpj)}</span>
              {client.phone && <span><strong>Tel:</strong> {client.phone}</span>}
              {client.email && <span><strong>Email:</strong> {client.email}</span>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-4 md:mt-0">
          <Button variant="outline" onClick={() => navigate(createPageUrl(`EditClient?id=${client.id}`))}>
            <Edit className="h-4 w-4 mr-2" /> Editar
          </Button>
          <Button variant="destructive">
            <Trash2 className="h-4 w-4 mr-2" /> Excluir
          </Button>
        </div>
      </div>

      {/* Abas Modernas */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-white rounded-lg shadow p-2 flex gap-2">
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="systems">Sistemas ({client.systems?.length || 0})</TabsTrigger>
          <TabsTrigger value="contacts">Contatos ({client.technical_contacts?.length || 0})</TabsTrigger>
          <TabsTrigger value="activity">Atividade</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Card Dados Cadastrais */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  Dados Cadastrais
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-gray-700">
                <div><strong>Razão Social:</strong> {client.corporate_name || '-'}</div>
                <div><strong>CNPJ:</strong> {formatCNPJ(client.cnpj)}</div>
                <div><strong>Telefone:</strong> {client.phone || '-'}</div>
                <div><strong>Email:</strong> {client.email || '-'}</div>
                {client.address && <div><strong>Endereço:</strong> {client.address}</div>}
                <div><strong>Status:</strong> <Badge className={client.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>{client.status === 'active' ? 'Ativo' : 'Inativo'}</Badge></div>
              </CardContent>
            </Card>
            {/* Card Contatos Técnicos */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Contatos Técnicos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {client.technical_contacts?.length > 0 ? (
                  <div className="space-y-2">
                    {client.technical_contacts.map((contact, index) => (
                      <div key={index} className="border-b pb-2 mb-2 last:border-0 last:pb-0 last:mb-0">
                        <div className="font-medium">{contact.name}</div>
                        <div className="text-xs text-muted-foreground flex items-center"><Mail className="h-4 w-4 mr-1" />{contact.email}</div>
                        {contact.phone && <div className="text-xs text-muted-foreground flex items-center"><Phone className="h-4 w-4 mr-1" />{contact.phone}</div>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground text-xs">Nenhum contato técnico cadastrado</div>
                )}
              </CardContent>
            </Card>
            {/* Card Acessos Remotos */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Link2 className="h-5 w-5 text-primary" />
                  Acessos Remotos
                </CardTitle>
              </CardHeader>
              <CardContent>
                {client.remote_access?.length > 0 ? (
                  <div className="space-y-2">
                    {client.remote_access.map((access, index) => (
                      <div key={index} className="border-b pb-2 mb-2 last:border-0 last:pb-0 last:mb-0">
                        <div className="font-medium flex items-center gap-2">
                          {access.type === 'teamviewer' && <Computer className="h-4 w-4" />}
                          {access.type === 'anydesk' && <Globe className="h-4 w-4" />}
                          {access.type === 'vpn' && <Network className="h-4 w-4" />}
                          {access.type === 'rdp' && <HardDrive className="h-4 w-4" />}
                          {access.type === 'ssh' && <Key className="h-4 w-4" />}
                          {access.type}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center"><Key className="h-4 w-4 mr-1" />ID: {access.identifier}</div>
                        {access.notes && <div className="text-xs text-muted-foreground">{access.notes}</div>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground text-xs">Nenhum acesso remoto configurado</div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="systems" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Cpu className="h-5 w-5 text-primary" />
                  Sistemas Utilizados
                </div>
                <Button variant="outline" size="sm">
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Adicionar Sistema
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {client.systems?.length > 0 ? (
                <div className="space-y-6">
                  {client.systems.map((system, index) => (
                    <div key={index} className="p-4 rounded-lg border bg-card">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-medium text-lg">{system.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            Versão: {system.version}
                          </p>
                        </div>
                        <Button variant="ghost" size="icon">
                          <Settings className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="space-y-2">
                          <div className="flex items-center text-muted-foreground">
                            <Globe className="h-4 w-4 mr-2" />
                            <a 
                              href={system.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {system.url}
                            </a>
                          </div>
                          <div className="flex items-center text-muted-foreground">
                            <HardDrive className="h-4 w-4 mr-2" />
                            {system.host}
                          </div>
                          <div className="flex items-center text-muted-foreground">
                            <Database className="h-4 w-4 mr-2" />
                            {system.database}
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center text-muted-foreground">
                            <Key className="h-4 w-4 mr-2" />
                            Usuário: {system.username}
                          </div>
                          {system.tool && (
                            <div>
                              {system.tool}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {system.notes && (
                        <>
                          <Separator className="my-4" />
                          <div className="text-sm text-muted-foreground">
                            {system.notes}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <Cpu className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhum sistema cadastrado</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contacts">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Contatos
                </div>
                <Button variant="outline" size="sm">
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Adicionar Contato
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {client.technical_contacts?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {client.technical_contacts.map((contact, index) => (
                    <Card key={index}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <Avatar>
                              <AvatarFallback>
                                {contact.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <h3 className="font-medium">{contact.name}</h3>
                              <p className="text-sm text-muted-foreground">{contact.role}</p>
                            </div>
                          </div>
                          <Button variant="ghost" size="icon">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center text-sm text-muted-foreground">
                            <Mail className="h-4 w-4 mr-2" />
                            {contact.email}
                          </div>
                          {contact.phone && (
                            <div className="flex items-center text-sm text-muted-foreground">
                              <Phone className="h-4 w-4 mr-2" />
                              {contact.phone}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhum contato cadastrado</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Histórico de Atividades
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-6 text-muted-foreground">
                <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Histórico de atividades em desenvolvimento</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
